import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getProject, setProjectStatus, deleteProject,
    createObstacle, deleteObstacle, bulkDeleteObstacles, uploadObstaclesCsv, getProjectMeta,
    updateProject, uploadRunwayProfile, getRunwayProfile,
} from '../api/client';
import { T } from '../assets/theme';
import { ConfirmModal } from '../components/UI';
import EditObstacleModal from '../components/EditObstacleModal';
import { HULL_MODE_KEY } from '../utils/convexHull';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const OBSTACLE_TYPES = ['Tower', 'Building', 'Crane', 'Wind Turbine', 'Chimney', 'Mast', 'Power Line', 'Bridge', 'Other'];

const EMPTY_OBS = {
    name: '', obstacle_type: 'Tower',
    lat_dd: '', lat_mm: '', lat_ss: '', lat_dir: 'S',
    lon_dd: '', lon_mm: '', lon_ss: '', lon_dir: 'E',
    elevation_m: '', h_tolerance: '', v_tolerance: '', description: '',
};

const CSV_TEMPLATE = `obstacle_name,type,latitude,longitude,elevation_m,h_tolerance,v_tolerance
Tower A,Tower,-26.1392,28.2459,1750,15,9
Mast B,Mast,-26.1500,28.2500,1800,10,6
`;

function decimalToDms(dd, isLat) {
    if (dd === null || dd === undefined || Number.isNaN(Number(dd))) return '';
    const value = Number(dd);
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const degrees = Math.floor(absValue);
    const minutesFull = (absValue - degrees) * 60;
    const minutes = Math.floor(minutesFull);
    const seconds = (minutesFull - minutes) * 60;
    return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toFixed(4).padStart(7, '0')}"${hemi}`;
}

function parseDmsStringToDecimal(dms) {
    if (!dms || typeof dms !== 'string') return null;
    const match = dms.trim().match(/^(\d{1,3})[°\s](\d{1,2})['’\s](\d{1,2}(?:\.\d+)?)['"”]?\s*([NSEWnsew])$/);
    if (!match) return null;
    const [, deg, min, sec, hemi] = match;
    let value = Number(deg) + Number(min) / 60 + Number(sec) / 3600;
    if (hemi.toUpperCase() === 'S' || hemi.toUpperCase() === 'W') value = -value;
    return value;
}

function formatCoordinate(value, dms, isLat, format) {
    if (format === 'dms') {
        if (dms) return dms;
        return decimalToDms(value, isLat) || '—';
    }
    if (value !== null && value !== undefined && value !== '') {
        return Number(value).toFixed(4);
    }
    if (dms) {
        const parsed = parseDmsStringToDecimal(dms);
        return parsed !== null ? parsed.toFixed(4) : dms;
    }
    return '—';
}

function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'obstacle_template.csv'; a.click();
    URL.revokeObjectURL(url);
}

function Btn({ children, onClick, variant = 'primary', small, disabled, style: sx = {} }) {
    const variants = {
        primary: { bg: T.blue, color: T.white },
        danger: { bg: 'rgba(232,64,64,0.15)', color: '#e84040' },
        ghost: { bg: 'rgba(58,138,196,0.09)', color: T.blue },
        success: { bg: 'rgba(46,201,139,0.15)', color: T.safe },
        warning: { bg: 'rgba(245,166,35,0.15)', color: T.warn },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button onClick={onClick} disabled={disabled} style={{
            background: v.bg, color: v.color, border: `1px solid ${v.color}33`,
            borderRadius: 6, padding: small ? '5px 12px' : '8px 18px',
            fontSize: small ? 11 : 13, fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', ...sx,
        }}>{children}</button>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending:   { bg: 'rgba(245,166,35,0.13)',  color: '#f5a623', label: 'PENDING' },
        assessing: { bg: 'rgba(58,138,196,0.13)',   color: '#3a8ac4', label: 'ASSESSING' },
        qa:        { bg: 'rgba(155,89,182,0.13)',   color: '#9b59b6', label: 'UNDER QA' },
        complete:  { bg: 'rgba(46,201,139,0.13)',   color: '#2ec98b', label: 'COMPLETE' },
        active:    { bg: 'rgba(245,166,35,0.13)',   color: '#f5a623', label: 'PENDING' },
        on_hold:   { bg: 'rgba(200,200,200,0.13)',  color: '#aaa',    label: 'ON HOLD' },
    };
    const s = map[status] || map.pending;
    return (
        <span style={{
            padding: '3px 10px', borderRadius: 3, border: `1px solid ${s.color}`,
            background: s.bg, color: s.color, fontSize: 10, fontWeight: 800, letterSpacing: '0.07em',
        }}>{s.label}</span>
    );
}

function Field({ label, value, half }) {
    return (
        <div style={{ flex: half ? '0 0 calc(50% - 7px)' : '1 1 100%' }}>
            <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13, color: T.white }}>{value || '—'}</div>
        </div>
    );
}

// ─── Manual obstacle form ─────────────────────────────────────────────────────
function ObstacleForm({ projectId, onAdded }) {
    const [form, setForm] = useState(EMPTY_OBS);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '6px 8px', color: T.white, fontSize: 11, outline: 'none', width: '100%', boxSizing: 'border-box',
    };

    async function submit() {
        if (!form.name.trim() || form.lat_dd === '' || form.lat_mm === '' || form.lat_ss === '' || form.lon_dd === '' || form.lon_mm === '' || form.lon_ss === '') {
            setErr('Name, and complete Latitude/Longitude DMS components are required'); return;
        }
        setLoading(true); setErr('');
        try {
            const lat_dms = `${form.lat_dd}°${form.lat_mm}'${form.lat_ss}"${form.lat_dir}`;
            const lon_dms = `${form.lon_dd}°${form.lon_mm}'${form.lon_ss}"${form.lon_dir}`;
            const payload = { ...form, lat_dms, lon_dms, project_id: projectId };
            delete payload.lat_dd; delete payload.lat_mm; delete payload.lat_ss; delete payload.lat_dir;
            delete payload.lon_dd; delete payload.lon_mm; delete payload.lon_ss; delete payload.lon_dir;

            const { data } = await createObstacle(payload);
            onAdded(data);
            setForm(EMPTY_OBS);
        } catch { setErr('Failed to add obstacle'); }
        finally { setLoading(false); }
    }

    const Label = ({ t }) => (
        <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{t}</div>
    );

    return (
        <div style={{
            background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 8, padding: 18,
        }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 14 }}>Add Obstacle Manually</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                    <Label t="Obstacle Name *" />
                    <input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder="e.g. Tower A" />
                </div>
                <div>
                    <Label t="Type" />
                    <select value={form.obstacle_type} onChange={e => set('obstacle_type', e.target.value)} style={inputStyle}>
                        {OBSTACLE_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div>
                    <Label t="Elevation (m AMSL)" />
                    <input type="number" value={form.elevation_m} onChange={e => set('elevation_m', e.target.value)} style={inputStyle} placeholder="e.g. 1750" />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                        <Label t="Latitude (DMS) *" />
                        <div style={{ display: 'flex', gap: 4 }}>
                            <input type="number" value={form.lat_dd} onChange={e => set('lat_dd', e.target.value)} style={{ ...inputStyle, width: '25%', padding: '6px 4px' }} placeholder="DD" />
                            <input type="number" value={form.lat_mm} onChange={e => set('lat_mm', e.target.value)} style={{ ...inputStyle, width: '25%', padding: '6px 4px' }} placeholder="MM" />
                            <input type="number" step="any" value={form.lat_ss} onChange={e => set('lat_ss', e.target.value)} style={{ ...inputStyle, width: '35%', padding: '6px 4px' }} placeholder="SS.SSSS" />
                            <select value={form.lat_dir} onChange={e => set('lat_dir', e.target.value)} style={{ ...inputStyle, width: '15%', padding: '6px 0px', WebkitAppearance: 'none' }}>
                                <option value="N">N</option>
                                <option value="S">S</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <Label t="Longitude (DMS) *" />
                        <div style={{ display: 'flex', gap: 4 }}>
                            <input type="number" value={form.lon_dd} onChange={e => set('lon_dd', e.target.value)} style={{ ...inputStyle, width: '25%', padding: '6px 4px' }} placeholder="DD" />
                            <input type="number" value={form.lon_mm} onChange={e => set('lon_mm', e.target.value)} style={{ ...inputStyle, width: '25%', padding: '6px 4px' }} placeholder="MM" />
                            <input type="number" step="any" value={form.lon_ss} onChange={e => set('lon_ss', e.target.value)} style={{ ...inputStyle, width: '35%', padding: '6px 4px' }} placeholder="SS.SSSS" />
                            <select value={form.lon_dir} onChange={e => set('lon_dir', e.target.value)} style={{ ...inputStyle, width: '15%', padding: '6px 0px', WebkitAppearance: 'none' }}>
                                <option value="E">E</option>
                                <option value="W">W</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div>
                    <Label t="H Tolerance (m)" />
                    <input type="number" value={form.h_tolerance} onChange={e => set('h_tolerance', e.target.value)} style={inputStyle} placeholder="±m horiz" />
                </div>
                <div>
                    <Label t="V Tolerance (m)" />
                    <input type="number" value={form.v_tolerance} onChange={e => set('v_tolerance', e.target.value)} style={inputStyle} placeholder="±m vert" />
                </div>
            </div>
            {err && <div style={{ color: '#e84040', fontSize: 10, marginTop: 8 }}>{err}</div>}
            <div style={{ marginTop: 12 }}>
                <Btn onClick={submit} disabled={loading} small>{loading ? 'Adding…' : '+ Add Obstacle'}</Btn>
            </div>
        </div>
    );
}

// ─── Obstacle row ─────────────────────────────────────────────────────────────
function ObstacleRow({ obs, selected, onToggleSelect, onDelete, onEdit, coordFormat }) {
    return (
        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
            <td style={{ padding: '7px 10px' }}>
                <input type="checkbox" checked={selected} onChange={() => onToggleSelect(obs.id)} />
            </td>
            <td style={{ padding: '7px 10px', fontSize: 12, color: T.white }}>{obs.name || '—'}</td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted }}>{obs.obstacle_type}</td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted, fontFamily: 'monospace' }}>
                {formatCoordinate(obs.latitude, obs.lat_dms, true, coordFormat)}
            </td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted, fontFamily: 'monospace' }}>
                {formatCoordinate(obs.longitude, obs.lon_dms, false, coordFormat)}
            </td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted }}>{obs.elevation_m ?? '—'}</td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted }}>{obs.h_tolerance ?? '—'}</td>
            <td style={{ padding: '7px 10px', fontSize: 11, color: T.muted }}>{obs.v_tolerance ?? '—'}</td>
            <td style={{ padding: '7px 10px', display: 'flex', gap: 6 }}>
                <button onClick={() => onEdit(obs)} style={{
                    background: 'rgba(58,138,196,0.12)', border: '1px solid rgba(58,138,196,0.24)',
                    color: T.blue, borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer',
                }}>✎</button>
                <button onClick={() => onDelete(obs.id)} style={{
                    background: 'rgba(232,64,64,0.12)', border: '1px solid #e8404044',
                    color: '#e84040', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer',
                }}>✕</button>
            </td>
        </tr>
    );
}

function EditProjectModal({ project, meta, onClose, onUpdate }) {
    const [form, setForm] = useState({
        name: project.name || '',
        client_id: project.client_id || '',
        client_name: project.client_name || '',
        assessor: project.assessor || '',
        verifier: project.verifier || '',
        obstacle_type: project.obstacle_type || '',
        due_date: project.due_date || '',
        project_type: project.project_type || 'aviation_obstacle_assessment',
        description: project.description || '',
        aerodrome: project.aerodrome || 'FAOR',
        airport_name: project.airport_name || '',
        airport_designator: project.airport_designator || '',
        survey_company: project.survey_company || '',
        survey_year: project.survey_year || '',
        rwy_profile_not_available: project.rwy_profile_not_available || false,
        caa_ref: project.caa_ref || '',
    });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '8px 12px', color: T.white, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
    };

    async function submit() {
        setLoading(true);
        try {
            const { data } = await updateProject(project.id, form);
            onUpdate(data);
            onClose();
        } finally { setLoading(false); }
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 32, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 24 }}>Edit Project Details</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Project Name</div>
                        <input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} /></div>
                    <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Client / Company</div>
                        <select value={form.client_id} onChange={e => set('client_id', e.target.value)} style={inputStyle}>
                            <option value="">— select —</option>
                            {(meta.clients || []).map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select></div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14 }}>
                        <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Assessor</div>
                            <select value={form.assessor} onChange={e => set('assessor', e.target.value)} style={inputStyle}>
                                <option value="">— select —</option>
                                {(meta.assessors || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select></div>
                        <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Verifier</div>
                            <select value={form.verifier} onChange={e => set('verifier', e.target.value)} style={inputStyle}>
                                <option value="">— select —</option>
                                {(meta.verifiers || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Obstacle Type</div>
                            <select value={form.obstacle_type} onChange={e => set('obstacle_type', e.target.value)} style={inputStyle}>
                                <option value="">— select —</option>
                                {(meta.obstacle_types || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select></div>
                        <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Due Date</div>
                            <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} style={inputStyle} /></div>
                    </div>

                    <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Project Type</div>
                        <select value={form.project_type} onChange={e => set('project_type', e.target.value)} style={inputStyle}>
                            <option value="aviation_obstacle_assessment">Aeronautical Assessment of Aviation Obstacles</option>
                            <option value="annex_14_ols_assessment">Annex 14 Obstacle Assessment Report (OLS Only)</option>
                        </select></div>

                    {form.project_type === 'annex_14_ols_assessment' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14, background: 'rgba(58,138,196,0.05)', padding: 12, borderRadius: 8, border: `1px solid ${T.blue}22` }}>
                            <div style={{ gridColumn: '1 / -1', fontSize: 12, fontWeight: 700, color: T.blue, marginBottom: -4 }}>OLS Report Information</div>
                            <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Airport Name</div>
                                <input value={form.airport_name} onChange={e => set('airport_name', e.target.value)} style={inputStyle} /></div>
                            <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Airport Designator</div>
                                <input value={form.airport_designator} onChange={e => set('airport_designator', e.target.value)} style={inputStyle} /></div>
                            <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Survey Company</div>
                                <input value={form.survey_company} onChange={e => set('survey_company', e.target.value)} style={inputStyle} /></div>
                            <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Survey Year</div>
                                <input value={form.survey_year} onChange={e => set('survey_year', e.target.value)} style={inputStyle} /></div>
                            
                            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                <input 
                                    type="checkbox" 
                                    id="rwy_not_avail"
                                    checked={form.rwy_profile_not_available} 
                                    onChange={e => set('rwy_profile_not_available', e.target.checked)} 
                                />
                                <label htmlFor="rwy_not_avail" style={{ fontSize: 13, cursor: 'pointer' }}>Runway Profile CSV Not Available</label>
                            </div>
                        </div>
                    )}

                    <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>CAA ID / Ref</div>
                        <input value={form.caa_ref} onChange={e => set('caa_ref', e.target.value)} style={inputStyle} placeholder="e.g. CAA/OAS/2024/001" /></div>
                    <div><div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? 'Updating…' : 'Save Changes'}</Btn>
                </div>
            </div>
        </div>
    );
}

// ─── Main ProjectPage ─────────────────────────────────────────────────────────
export default function ProjectPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileRef = useRef();
    const rwyFileRef = useRef();

    const [project, setProject] = useState(null);
    const [meta, setMeta] = useState({ assessors: [], verifiers: [], obstacle_types: [] });
    const [loading, setLoading] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [rwyProfile, setRwyProfile] = useState(null);
    const [rwyLoading, setRwyLoading] = useState(false);
    const [csvResult, setCsvResult] = useState(null);
    const [selectedObs, setSelectedObs] = useState([]);
    const [coordFormat, setCoordFormat] = useState('decimal');
    const [editingObstacle, setEditingObstacle] = useState(null);
    const [obsPage, setObsPage] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'single'|'bulk', id?: number }
    const [hullMode, setHullMode] = useState(false);
    const OBS_PER_PAGE = 10;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: pData }, { data: mData }, { data: rData }] = await Promise.all([
                getProject(id), 
                getProjectMeta(),
                getRunwayProfile(id)
            ]);
            setProject(pData);
            setMeta(mData);
            setRwyProfile(rData);
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Sync hull mode from localStorage once project id is known
    useEffect(() => {
        if (!id) return;
        setHullMode(localStorage.getItem(HULL_MODE_KEY(id)) === 'true');
    }, [id]);

    function toggleHullMode() {
        setHullMode(prev => {
            const next = !prev;
            if (next) localStorage.setItem(HULL_MODE_KEY(id), 'true');
            else localStorage.removeItem(HULL_MODE_KEY(id));
            return next;
        });
    }

    async function handleStatus() {
        const cycle = { pending: 'assessing', active: 'assessing', assessing: 'qa', qa: 'complete', complete: 'pending' };
        const next = cycle[project.status] || 'assessing';
        try {
            const { data } = await setProjectStatus(id, next);
            setProject(p => ({ ...p, status: data.status }));
        } catch (e) {
            if (e.response?.status === 400) {
                alert(e.response.data?.detail || 'Complete the QA sign-off checklist before marking this project complete.');
                navigate(`/projects/${id}/assess?tab=qa`);
            } else {
                throw e;
            }
        }
    }

    async function handleHoldToggle() {
        const next = project.status === 'on_hold' ? 'pending' : 'on_hold';
        const { data } = await setProjectStatus(id, next);
        setProject(p => ({ ...p, status: data.status }));
    }

    async function handleExportAixm() {
        setExportingAixm(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${id}/export/aixm`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // The server resolves the real aerodrome(s) from obstacle
            // proximity, not the project's own (often-stale/default-'FAOR')
            // `aerodrome` field — read its filename back off the response
            // rather than reconstructing one from that field here too.
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${id}_export_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    async function handleDeleteObs(obsId) {
        setDeleteConfirm({ type: 'single', id: obsId });
    }

    async function handleBulkDelete() {
        if (!selectedObs.length) return;
        setDeleteConfirm({ type: 'bulk' });
    }

    function handleDeleteAllObs() {
        if (!obstacles.length) return;
        setDeleteConfirm({ type: 'all' });
    }

    async function confirmDelete() {
        if (!deleteConfirm) return;

        if (deleteConfirm.type === 'single') {
            const obsId = deleteConfirm.id;
            await deleteObstacle(obsId);
            setProject(p => {
                const updated = p.obstacles.filter(o => o.id !== obsId);
                const maxPage = Math.max(0, Math.ceil(updated.length / OBS_PER_PAGE) - 1);
                setObsPage(prev => Math.min(prev, maxPage));
                return { ...p, obstacles: updated };
            });
            setSelectedObs(prev => prev.filter(id => id !== obsId));
        } else if (deleteConfirm.type === 'all') {
            await bulkDeleteObstacles(id, { delete_all: true });
            setObsPage(0);
            setSelectedObs([]);
            setProject(p => ({ ...p, obstacles: [] }));
        } else {
            await bulkDeleteObstacles(id, { obstacle_ids: selectedObs });
            setProject(p => {
                const updated = p.obstacles.filter(o => !selectedObs.includes(o.id));
                const maxPage = Math.max(0, Math.ceil(updated.length / OBS_PER_PAGE) - 1);
                setObsPage(prev => Math.min(prev, maxPage));
                return { ...p, obstacles: updated };
            });
            setSelectedObs([]);
        }
        setDeleteConfirm(null);
    }

    const toggleSelectObs = (obsId) => {
        setSelectedObs(prev => prev.includes(obsId) ? prev.filter(id => id !== obsId) : [...prev, obsId]);
    };

    const toggleSelectAll = (pageObstacles) => {
        const pageIds = pageObstacles.map(o => o.id);
        const allPageSelected = pageIds.every(id => selectedObs.includes(id));
        if (allPageSelected) {
            setSelectedObs(prev => prev.filter(id => !pageIds.includes(id)));
        } else {
            setSelectedObs(prev => [...new Set([...prev, ...pageIds])]);
        }
    };

    async function handleCsv(e) {
        const file = e.target.files[0];
        if (!file) return;
        setCsvLoading(true); setCsvResult(null);
        try {
            const { data } = await uploadObstaclesCsv(id, file);
            setCsvResult(data);
            await load();
        } finally {
            setCsvLoading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    }

    if (loading) return <div style={{ padding: 32, color: T.muted }}>Loading…</div>;
    if (!project) return <div style={{ padding: 32, color: '#e84040' }}>Project not found.</div>;

    const obstacles = project.obstacles || [];
    const totalPages = Math.max(1, Math.ceil(obstacles.length / OBS_PER_PAGE));
    const safeObsPage = Math.min(obsPage, totalPages - 1);
    const pageObstacles = obstacles.slice(safeObsPage * OBS_PER_PAGE, (safeObsPage + 1) * OBS_PER_PAGE);

    return (
        <div style={{ padding: 28, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 28 }}>
                <button onClick={() => navigate('/projects')} style={{
                    background: 'none', border: `1px solid ${T.border}`, borderRadius: 6,
                    color: T.muted, padding: '6px 12px', cursor: 'pointer', fontSize: 13, flexShrink: 0,
                }}>← Back</button>

                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{project.name}</h2>
                        <StatusBadge status={project.status} />
                    </div>
                    {project.client_name && (
                        <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>🏢 {project.client_name}</div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                    <Btn variant="ghost" onClick={() => setShowEdit(true)}>✎ Edit Details</Btn>
                    <Btn
                        variant="ghost"
                        onClick={handleExportAixm}
                        disabled={exportingAixm}
                        title="Export obstacles and aerodrome reference data as AIXM 5.1"
                    >
                        {exportingAixm ? '⏳ Exporting…' : '📤 Export AIXM'}
                    </Btn>
                    {project.status === 'qa' && (
                        <Btn variant="ghost" onClick={() => navigate(`/projects/${id}/assess?tab=qa`)}>
                            🧾 Continue QA Sign-Off
                        </Btn>
                    )}
                    {project.status === 'on_hold' ? (
                        <Btn variant="warning" onClick={handleHoldToggle}>▶ Resume</Btn>
                    ) : (
                        <>
                            <Btn variant="warning" onClick={handleStatus}>
                                {{
                                    pending:   '▶ Start Assessing',
                                    active:    '▶ Start Assessing',
                                    assessing: '⬆ Send to QA',
                                    qa:        '✓ Mark Complete',
                                    complete:  '↩ Reopen',
                                }[project.status] || '▶ Start Assessing'}
                            </Btn>
                            <Btn variant="ghost" onClick={handleHoldToggle}>⏸ Hold</Btn>
                        </>
                    )}
                    <Btn
                        variant="success"
                        onClick={() => navigate(`/projects/${id}/assess`)}
                        disabled={obstacles.length === 0}
                    >
                        🗺 Open Assessment
                    </Btn>
                </div>
            </div>

            {/* Project info grid */}
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10,
                padding: '20px 24px', marginBottom: 24,
                display: 'flex', flexWrap: 'wrap', gap: 20,
            }}>
                <Field label="Assessor" value={project.assessor} half />
                <Field label="Verifier" value={project.verifier} half />
                <Field label="Obstacle Type" value={project.obstacle_type} half />
                <Field label="Due Date" value={project.due_date} half />
                {project.project_type === 'annex_14_ols_assessment' && (
                    <>
                        <Field label="Airport Name" value={project.airport_name} half />
                        <Field label="Airport Designator" value={project.airport_designator} half />
                        <Field label="Survey Company" value={project.survey_company} half />
                        <Field label="Survey Year" value={project.survey_year} half />
                    </>
                )}
                {project.caa_ref && <Field label="CAA ID / Ref" value={project.caa_ref} half />}
                {project.description && <Field label="Description" value={project.description} />}
            </div>

            {project.project_type === 'annex_14_ols_assessment' && (
                <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: T.muted }}>Runway Profile Data</div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {project.rwy_profile_not_available ? (
                                <div style={{ fontSize: 12, color: T.warning, fontWeight: 600 }}>⚠ Not Available (Using Threshold Points)</div>
                            ) : (
                                <>
                                    <div style={{ fontSize: 12, color: rwyProfile?.points?.length ? T.safe : T.muted }}>
                                        {rwyProfile?.points?.length ? `✓ ${rwyProfile.points.length} points uploaded` : 'No CSV uploaded'}
                                    </div>
                                    <Btn variant="ghost" small onClick={() => rwyFileRef.current?.click()} disabled={rwyLoading}>
                                        {rwyLoading ? 'Uploading…' : '↑ Upload Profile CSV'}
                                    </Btn>
                                </>
                            )}
                        </div>
                    </div>
                    
                    <input 
                        type="file" 
                        ref={rwyFileRef} 
                        style={{ display: 'none' }} 
                        accept=".csv"
                        onChange={async e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setRwyLoading(true);
                            try {
                                await uploadRunwayProfile(project.id, file);
                                const { data } = await getRunwayProfile(project.id);
                                setRwyProfile(data);
                            } catch (err) {
                                alert("Failed to upload runway profile: " + (err.response?.data?.detail || err.message));
                            } finally {
                                setRwyLoading(false);
                            }
                        }}
                    />

                    {!project.rwy_profile_not_available && !rwyProfile?.points?.length && (
                        <div style={{ fontSize: 11, color: T.muted, background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 6, textAlign: 'center' }}>
                            Upload a CSV with <b>distance_m</b> and <b>elevation_m</b> columns to draw the runway profile graph.
                        </div>
                    )}
                </div>
            )}


            {/* Obstacle loader */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 24 }}>
                <ObstacleForm projectId={Number(id)} onAdded={obs => setProject(p => ({ ...p, obstacles: [obs, ...p.obstacles] }))} />

                {/* CSV upload */}
                <div style={{
                    background: T.bg0, border: `1px dashed ${T.border}`, borderRadius: 8, padding: 18,
                    display: 'flex', flexDirection: 'column', gap: 12,
                }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Upload via CSV</div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                        Upload obstacles in bulk. Download the template for the correct format.
                    </div>
                    <input ref={fileRef} type="file" accept=".csv" onChange={handleCsv} style={{ color: T.white, fontSize: 12 }} />
                    {csvLoading && <div style={{ fontSize: 11, color: T.muted }}>Uploading…</div>}
                    {csvResult && (
                        <div style={{ fontSize: 11 }}>
                            <span style={{ color: T.safe }}>✓ {csvResult.inserted} inserted</span>
                            {csvResult.errors?.length > 0 && (
                                <span style={{ color: '#e84040', marginLeft: 10 }}>✕ {csvResult.errors.length} errors</span>
                            )}
                        </div>
                    )}
                    <Btn variant="ghost" small onClick={downloadTemplate}>⬇ Download Template</Btn>
                </div>
            </div>

            {/* Obstacle table */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>
                                Obstacles <span style={{ color: T.muted, fontWeight: 400, marginLeft: 8 }}>({obstacles.length})</span>
                            </div>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.muted, cursor: 'pointer' }}>
                                <input type="checkbox" checked={coordFormat === 'dms'} onChange={e => setCoordFormat(e.target.checked ? 'dms' : 'decimal')} />
                                Show DD:MM:SS.SSSS
                            </label>
                            {/* Hull mode toggle */}
                            <button
                                onClick={toggleHullMode}
                                title="When enabled, obstacles sharing the same name are treated as building corners. The convex hull is computed and perimeter points are interpolated at 10m spacing, all using the group's maximum elevation. This ensures full coverage when assessing buildings."
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: hullMode ? 'rgba(46,201,139,0.12)' : 'rgba(255,255,255,0.04)',
                                    border: `1px solid ${hullMode ? T.safe : T.border}`,
                                    borderRadius: 5, padding: '3px 10px',
                                    color: hullMode ? T.safe : T.muted,
                                    fontSize: 11, fontWeight: hullMode ? 700 : 400,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <span style={{ fontSize: 14 }}>⬡</span>
                                Assess as Hull {hullMode ? 'ON' : 'OFF'}
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                        {selectedObs.length > 0 && (
                            <Btn variant="danger" small onClick={handleBulkDelete}>
                                Delete Selected ({selectedObs.length})
                            </Btn>
                        )}
                        {obstacles.length > 0 && (
                            <Btn variant="danger" small onClick={handleDeleteAllObs}>
                                Delete All ({obstacles.length})
                            </Btn>
                        )}
                        </div>
                </div>

                {obstacles.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                        No obstacles loaded yet. Add them above.
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: T.bg0 }}>
                                        <th style={{ padding: '10px 12px', width: 30 }}>
                                            <input
                                                type="checkbox"
                                                checked={pageObstacles.length > 0 && pageObstacles.every(o => selectedObs.includes(o.id))}
                                                onChange={() => toggleSelectAll(pageObstacles)}
                                            />
                                        </th>
                                        {['Name', 'Type', 'Latitude', 'Longitude', 'Elev (m)', 'H Tol', 'V Tol', ''].map(h => (
                                            <th key={h} style={{ padding: '7px 10px', fontSize: 9, fontWeight: 700, color: T.muted, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageObstacles.map(o => (
                                        <ObstacleRow key={o.id} obs={o} selected={selectedObs.includes(o.id)} onToggleSelect={toggleSelectObs} onDelete={handleDeleteObs} onEdit={setEditingObstacle} coordFormat={coordFormat} />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination tabs */}
                        {totalPages > 1 && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '10px 16px', borderTop: `1px solid ${T.border}`,
                                flexWrap: 'wrap',
                            }}>
                                <span style={{ fontSize: 11, color: T.muted, marginRight: 6 }}>
                                    {safeObsPage * OBS_PER_PAGE + 1}–{Math.min((safeObsPage + 1) * OBS_PER_PAGE, obstacles.length)} of {obstacles.length}
                                </span>
                                <button
                                    onClick={() => setObsPage(p => Math.max(0, p - 1))}
                                    disabled={safeObsPage === 0}
                                    style={{
                                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                                        color: safeObsPage === 0 ? T.muted : T.blue, cursor: safeObsPage === 0 ? 'default' : 'pointer',
                                        padding: '3px 9px', fontSize: 13, opacity: safeObsPage === 0 ? 0.4 : 1,
                                    }}
                                >
                                    ‹
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setObsPage(i)}
                                        style={{
                                            background: i === safeObsPage ? T.blue : 'none',
                                            border: `1px solid ${i === safeObsPage ? T.blue : T.border}`,
                                            borderRadius: 4, color: i === safeObsPage ? T.white : T.muted,
                                            cursor: 'pointer', padding: '3px 9px', fontSize: 12, fontWeight: i === safeObsPage ? 700 : 400,
                                            minWidth: 30,
                                        }}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setObsPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={safeObsPage === totalPages - 1}
                                    style={{
                                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                                        color: safeObsPage === totalPages - 1 ? T.muted : T.blue,
                                        cursor: safeObsPage === totalPages - 1 ? 'default' : 'pointer',
                                        padding: '3px 9px', fontSize: 13,
                                        opacity: safeObsPage === totalPages - 1 ? 0.4 : 1,
                                    }}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {obstacles.length === 0 && (
                <div style={{ fontSize: 11, color: T.muted, textAlign: 'center', marginTop: 12 }}>
                    Add at least one obstacle to enable the Assessment page.
                </div>
            )}

            {deleteConfirm && (
                <ConfirmModal
                    title={deleteConfirm.type === 'single' ? "Delete Obstacle" : deleteConfirm.type === 'all' ? "Delete All Obstacles" : "Bulk Delete"}
                    message={deleteConfirm.type === 'single'
                        ? "Are you sure you want to delete this obstacle? This action cannot be undone."
                        : deleteConfirm.type === 'all'
                        ? `Are you sure you want to delete all ${obstacles.length} obstacles in this project? This action cannot be undone.`
                        : `Are you sure you want to delete ${selectedObs.length} selected obstacles? This action cannot be undone.`
                    }
                    confirmText={deleteConfirm.type === 'single' ? "Delete Obstacle" : deleteConfirm.type === 'all' ? `Delete All ${obstacles.length}` : `Delete ${selectedObs.length} Obstacles`}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteConfirm(null)}
                    variant="danger"
                />
            )}

            {editingObstacle && (
                <EditObstacleModal
                    obstacle={editingObstacle}
                    onClose={() => setEditingObstacle(null)}
                    onUpdated={updatedObs => setProject(p => ({
                        ...p,
                        obstacles: p.obstacles.map(o => o.id === updatedObs.id ? updatedObs : o),
                    }))}
                />
            )}

            {showEdit && (
                <EditProjectModal
                    project={project}
                    meta={meta}
                    onClose={() => setShowEdit(false)}
                    onUpdate={data => setProject(data)}
                />
            )}
        </div>
    );
}
